import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { type Field, PuiTextField, Text } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import { removeNonAsciiCharacters } from '~/utils'

const useStyles = makeStyles(
  (theme) => ({
    radioLabel: {
      fontSize: '1.6rem',
      marginRight: theme.spacing(3),
    },
    textFieldInputReadOnly: {
      backgroundColor: theme.colors.tableOddRowBackground,
      border: 'none',
      borderRadius: 2,
      paddingTop: 5,
      paddingBottom: 5,
    },
    textFieldNoBorder: {
      border: 'none',
    },
    helperText: {
      marginLeft: 0,
      marginRight: 0,
      color: theme.colors.grayGray2,
      fontSize: '1rem',
    },
  }),
  { name: 'AdministrationInstructions' },
)

export interface AdministrationInstructionsProps {
  autofillField: Field
  disabled?: boolean
  instructionsField: Field
  instructionsRequired?: boolean
  isChewy: boolean
  simplified?: boolean
}

const AdministrationInstructions = ({
  autofillField,
  disabled,
  instructionsField,
  instructionsRequired = false,
  isChewy,
  simplified,
}: AdministrationInstructionsProps) => {
  const classes = useStyles()

  const { t } = useTranslation(['Common', 'Dialogs', 'Tooltips'])

  const AdministrationInstructionsInputMode = {
    CUSTOM: t('Common:WRITE_MY_OWN_PRESCRIPTION'),
    AUTOFILL: t('Dialogs:PRESCRIPTION_DIALOG.AUTOFILL'),
  }

  const isCustomChewyNotesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CHEWY_PRESCRIPTION_NOTES),
  )

  const showCustomChewyNotes = isChewy && isCustomChewyNotesEnabled

  const customSetNotesValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Limit the length of the notes to 280 characters if Chewy notes are enabled
    const value = removeNonAsciiCharacters(e.target.value.slice(0, 280))
    instructionsField.setValue(value)
    instructionsField.set({ ...e, target: { ...e.target, value } })
  }

  const isReadOnly = Boolean(autofillField?.value)

  return (
    <Grid container direction="column">
      {!simplified && (
        <>
          {(!isChewy || isCustomChewyNotesEnabled) && (
            <>
              <Text strong variant="body2">
                {t('Dialogs:PRESCRIPTION_DIALOG.ADMINISTRATION_INSTRUCTIONS')}
                &nbsp;
                <Text component="span" variant="body2">
                  ({t('Dialogs:PRESCRIPTION_DIALOG.APPEARS_ON_LABEL')})
                </Text>
              </Text>
              <RadioGroup
                row
                aria-label="label-section"
                name="administrationInstructionsInputMode"
                value={
                  isReadOnly
                    ? AdministrationInstructionsInputMode.AUTOFILL
                    : AdministrationInstructionsInputMode.CUSTOM
                }
                onChange={(_, value) =>
                  autofillField.setValue(
                    value === AdministrationInstructionsInputMode.AUTOFILL,
                  )
                }
              >
                {Object.values(AdministrationInstructionsInputMode).map(
                  (mode) => (
                    <FormControlLabel
                      classes={{ label: classes.radioLabel }}
                      control={<Radio />}
                      key={mode}
                      label={mode}
                      value={mode}
                    />
                  ),
                )}
              </RadioGroup>
            </>
          )}
        </>
      )}

      <Tooltip
        enterDelay={300}
        placement="top-end"
        title={
          simplified || (isChewy && !isCustomChewyNotesEnabled)
            ? ''
            : isReadOnly
              ? t('Tooltips:CLICK_WRITE_MY_OWN_TO_EDIT')
              : ''
        }
      >
        <PuiTextField
          multiline
          FormHelperTextProps={{
            className: classes.helperText,
          }}
          InputProps={{
            readOnly: isReadOnly,
            inputProps: { maxLength: 1000 },
            className: isReadOnly ? classes.textFieldInputReadOnly : undefined,
            classes: {
              notchedOutline: isReadOnly
                ? classes.textFieldNoBorder
                : undefined,
            },
          }}
          disabled={disabled}
          field={{
            ...instructionsField,
            set: showCustomChewyNotes
              ? customSetNotesValue
              : instructionsField.set,
          }}
          helperText={
            showCustomChewyNotes && !isReadOnly
              ? t('Dialogs:PRESCRIPTION_DIALOG.NOTES_HELPER_TEXT', {
                  notesLength: instructionsField.value?.length || 0,
                })
              : undefined
          }
          margin="none"
          minRows={isReadOnly ? 1 : 2}
          placeholder={
            isReadOnly
              ? ''
              : `${t('Common:INSTRUCTIONS')}${instructionsRequired ? '*' : ''}`
          }
          variant="outlined"
        />
      </Tooltip>
    </Grid>
  )
}

export default AdministrationInstructions
